import { createBrowserRouter, Outlet, redirect, RouterProvider } from 'react-router-dom';

import AcceptedPlansPage from 'components/acceptedPlans/AcceptedPlansPage';
import AllPatientsPage from 'components/allPatients/AllPatients';
import AuthError from 'components/AuthError';
import CoordinateLayout from 'components/coordinate/CoordinateLayout';
import CoordinatePage from 'components/coordinate/CoordinatePage';
import External from 'components/External';
import { Routes as InsightsRoutes } from 'components/insights/constants';
import InsightsContainer from 'components/insights/InsightsContainer';
import ActivitiesPerPatientWeekDetail from 'components/insights/layout/details/activitiesPerPatientWeek/ActivitiesPerPatientWeekDetail';
import AverageLengthOfStayDetail from 'components/insights/layout/details/averageLengthOfStay/AverageLengthOfStayDetail';
import OlioEngagementScoreDetail from 'components/insights/layout/details/olioEngagementScore/OlioEngagementScoreDetail';
import ReadmissionRateDetail from 'components/insights/layout/details/readmissionRate/ReadmissionRateDetail';
import ResponseTimeDetail from 'components/insights/layout/details/responseTime/ResponseTimeDetail';
import TotalDischargesDetail from 'components/insights/layout/details/totalDischarges/TotalDischargesDetail';
import MetricsDashboard from 'components/insights/layout/MetricsDashboard';
import AddPatientPage from 'components/intake/AddPatientPage';
import CoordinatePatientPage from 'components/intake/CoordinatePatientPage';
import EditPatientPage from 'components/intake/EditPatientPage';
import MainLayout from 'components/layouts/MainLayout';
import LoggedOut from 'components/LoggedOut';
import NotificationPreferences from 'components/notificationPreferences/NotificationPreferences';
import ConcurrentReviewsPage from 'components/patient/concurrentReview/ConcurrentReviewsPage';
import PatientStoryPage from 'components/patient/PatientStoryPage';
import PortfolioPage from 'components/portfolio/Portfolio';
import RootProviders from 'components/Root';
import ThemeProvider from 'components/ThemeProvider';
import { EpisodeProvider } from 'context/episode';
import { SelectedLocationEpisodeProvider } from 'context/locationEpisode';
import { AuthorizationProvider } from 'context/review/authorization';
import { ReferralProvider } from 'context/review/referral';
import { EscalationTypes } from 'models/Escalation';

import FilesPage from './components/patient/FilesPage';
import PatientPage from './components/patient/Patient';
import ReferralPage from './components/referral/ReferralPage';
import ReferralsListPage from './components/referrals/ReferralsListPage';

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootProviders />,
    children: [
      {
        index: true,
        loader: async () => redirect('/patients'),
      },
      {
        path: '/auth/callback',
        element: <></>,
      },
      {
        path: '/external',
        element: <External />,
      },
      {
        path: '/',
        element: <MainLayout />,
        children: [
          {
            path: '/patients/:episodeId/*',
            element: (
              <EpisodeProvider>
                <SelectedLocationEpisodeProvider>
                  <ReferralProvider>
                    <AuthorizationProvider>
                      <Outlet />
                    </AuthorizationProvider>
                  </ReferralProvider>
                </SelectedLocationEpisodeProvider>
              </EpisodeProvider>
            ),
            children: [
              {
                path: '*',
                element: <PatientPage />,
                children: [
                  {
                    path: '*',
                    element: <PatientStoryPage />,
                    children: [
                      {
                        path: 'authorization/:authorizationId/*',
                        element: null,
                      },
                    ],
                  },
                  {
                    path: 'concurrent-reviews',
                    element: <ConcurrentReviewsPage />,
                    children: [
                      {
                        path: ':authorizationId/*',
                        element: null,
                      },
                    ],
                  },
                  {
                    path: 'referrals',
                    element: <ReferralPage />,
                    children: [
                      {
                        path: ':referralId/*',
                        element: null,
                      },
                    ],
                  },
                  {
                    path: 'files',
                    element: <FilesPage />,
                  },
                ],
              },
              {
                path: 'edit',
                element: <EditPatientPage />,
              },
            ],
          },
          {
            path: '/all-patients',
            element: <AllPatientsPage />,
          },
          {
            path: '/contracted-health-plans',
            element: <AcceptedPlansPage />,
          },
          {
            path: '/referrals',
            element: <ReferralsListPage />,
            children: [
              {
                path: ':referralId/*',
                element: null,
              },
            ],
          },
          {
            path: '/insights/*',
            element: <InsightsContainer />,
            children: [
              {
                path: '*',
                element: <MetricsDashboard />,
              },
              {
                path: InsightsRoutes.OLIO_ENGAGEMENT_SCORE,
                element: <OlioEngagementScoreDetail />,
              },
              {
                path: InsightsRoutes.READMISSION_RATE,
                element: <ReadmissionRateDetail />,
              },
              {
                path: InsightsRoutes.AVERAGE_LENGTH_OF_STAY,
                element: <AverageLengthOfStayDetail />,
              },
              {
                path: InsightsRoutes.TOTAL_DISCHARGES,
                element: <TotalDischargesDetail />,
              },
              {
                path: InsightsRoutes.ACTIVITIES_PER_PATIENT_PER_WEEK,
                element: <ActivitiesPerPatientWeekDetail />,
              },
              {
                path: InsightsRoutes.PRIORITIES_NOTE_RESPONSE,
                element: <ResponseTimeDetail escalationType={EscalationTypes.PriorityNote} />,
              },
              {
                path: InsightsRoutes.ESCALATIONS_RESPONSE,
                element: <ResponseTimeDetail escalationType={EscalationTypes.Escalation} />,
              },
            ],
          },
          {
            path: '/coordinate',
            element: <CoordinateLayout />,
            children: [
              { index: true, element: <CoordinatePage /> },
              { path: ':id', element: <CoordinatePatientPage /> },
            ],
          },
          {
            path: '/connect-patients',
            loader: async () => redirect('/coordinate'),
          },
          {
            path: '/patients',
            element: <PortfolioPage />,
          },
          {
            path: '/patients/new',
            element: <AddPatientPage />,
          },
          {
            path: '/preferences',
            element: <NotificationPreferences />,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: (
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    ),
    children: [
      {
        path: '/logged-out',
        element: <LoggedOut />,
      },
      {
        path: '/error',
        element: <AuthError />,
      },
    ],
  },
  {
    path: '*',
    index: true,
    loader: async () => redirect('/patients'),
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
